import { IUserDenormalized } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { Link } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { DadoLogoWithName } from '../../../../../../components/DadoLogo';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../layout/Flex';
import { toFullName } from '../../../../../../services/user';
import { getNonImpersonationUrl, isImpersonation } from '../../services/routes';

const Container = styled('div')((p) => ({
  width: '100%',
  backgroundColor: p.theme.custom.colors.yellow.w300,
  padding: p.theme.spacing(3)
}));

export const ImpersonationBanner = ({ user }: { user: IUserDenormalized }) => {
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Container>
      <FlexContainerVertical
        justifyContent="center"
        alignItems="center"
        fullWidth
        spacing={1}
      >
        <DadoLogoWithName size={null} variant="black" />
        <Typography variant="h3">
          {t(`You're impersonating {{ name }}`, { name: toFullName(user) })}
        </Typography>
        <Typography variant="body1">
          {t(
            `The screen below is {{ name }}'s Experience Hub. If you complete any tasks below, it's as if {{ name }} completed those tasks.`,
            { name: user.preferredName || user.firstName }
          )}
        </Typography>
        <Typography variant="body1">
          {t(
            `For a shareable link to {{ name }}'s Experience Hub that does not include this yellow banner, click the button below.`,
            { name: user.preferredName || user.firstName }
          )}
        </Typography>

        <Button
          variant="outlined"
          startIcon={<Link size={16} />}
          color="inherit"
          onClick={() => {
            const url = getNonImpersonationUrl();
            navigator.clipboard.writeText(url);
            enqueueSnackbar(t('Shareable link copied!'), {
              variant: 'success'
            });
          }}
        >
          {t('Get a link to share with {{ name }}', {
            name: user.preferredName || user.firstName
          })}
        </Button>
      </FlexContainerVertical>
    </Container>
  );
};

export const MaybeImpersonationBanner = ({
  user
}: {
  user: IUserDenormalized;
}) => {
  const active = isImpersonation();
  return active ? <ImpersonationBanner user={user} /> : null;
};
